import type { SwipeOutcome, SwipeVector } from '@/lib/swipe-math';
import type { Zone } from '@/lib/zones';

const GOAL_W_RATIO = 0.86;
const GOAL_H_RATIO = 0.34;
const GOAL_Y_RATIO = 0.30;
const SPOT_Y_RATIO = 0.78;
const NO_POWER_REACH = 0.4;
const WIDE_OFFSET_PX = 60;

type Dims = { width: number; height: number };

type Geometry = {
  goalX: number;
  goalY: number;
  goalW: number;
  goalH: number;
  zoneW: number;
  zoneH: number;
  spotY: number;
  rowTopY: number;
  rowBotY: number;
};

function geometry({ width, height }: Dims): Geometry {
  const goalW = width * GOAL_W_RATIO;
  const goalH = height * GOAL_H_RATIO;
  const goalX = width / 2;
  const goalY = height * GOAL_Y_RATIO;
  const zoneW = goalW / 3;
  const zoneH = goalH / 2;
  return {
    goalX,
    goalY,
    goalW,
    goalH,
    zoneW,
    zoneH,
    spotY: height * SPOT_Y_RATIO,
    rowTopY: goalY - goalH / 2 + zoneH / 2,
    rowBotY: goalY - goalH / 2 + zoneH * 1.5,
  };
}

function zoneCenter(zone: Zone, g: Geometry): { x: number; y: number } {
  const colMap: Record<'L' | 'C' | 'R', number> = { L: 0, C: 1, R: 2 };
  const col = colMap[zone[0] as 'L' | 'C' | 'R'];
  const rowY = zone[1] === 'A' ? g.rowTopY : g.rowBotY;
  return { x: g.goalX - g.goalW / 2 + g.zoneW / 2 + col * g.zoneW, y: rowY };
}

function sideForOff(intended: SwipeOutcome['intendedZone'], vector: SwipeVector): 'L' | 'R' {
  if (intended !== 'off') {
    if (intended[0] === 'L') return 'L';
    if (intended[0] === 'R') return 'R';
  }
  return vector.dx < 0 ? 'L' : 'R';
}

function rowForOff(intended: SwipeOutcome['intendedZone'], g: Geometry, vector: SwipeVector): number {
  if (intended !== 'off') return intended[1] === 'A' ? g.rowTopY : g.rowBotY;
  return -vector.dy > 250 ? g.rowTopY : g.rowBotY;
}

export function ballTarget(swipe: SwipeOutcome, vector: SwipeVector, dims: Dims): { x: number; y: number } {
  const g = geometry(dims);

  if (swipe.actualZone !== 'off') return zoneCenter(swipe.actualZone, g);

  const reason = swipe.offFrameReason ?? 'no-power';

  if (reason === 'wide') {
    const side = sideForOff(swipe.intendedZone, vector);
    return {
      x: side === 'L' ? g.goalX - g.goalW / 2 - WIDE_OFFSET_PX : g.goalX + g.goalW / 2 + WIDE_OFFSET_PX,
      y: rowForOff(swipe.intendedZone, g, vector),
    };
  }

  if (reason === 'post') {
    const side = sideForOff(swipe.intendedZone, vector);
    return {
      x: side === 'L' ? g.goalX - g.goalW / 2 : g.goalX + g.goalW / 2,
      y: rowForOff(swipe.intendedZone, g, vector),
    };
  }

  // no-power: roll along swipe direction by 40% of spot→goal-centre vertical span.
  const r = Math.sqrt(vector.dx * vector.dx + vector.dy * vector.dy);
  const distance = (g.spotY - g.goalY) * NO_POWER_REACH;
  if (r === 0) return { x: g.goalX, y: g.spotY - distance };
  return {
    x: g.goalX + (vector.dx / r) * distance,
    y: g.spotY + (vector.dy / r) * distance,
  };
}
